import React, { useMemo } from 'react';
import { View, StyleSheet, Pressable, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import { FontSizes, vs } from '@/Utils/Dimensions';
import { useMediaQuery } from '@/Hooks/useMediaQuery/useMediaQuery';
import { useNavigation } from '@react-navigation/native';
import { ScreenName } from '@/navigators/StackConstants';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { MaterialIcons } from '@expo/vector-icons';
import { Animated, Easing } from 'react-native';

const AnimatedPuzzleButton = ({ onPress, isMobile }: { onPress: () => void, isMobile?: boolean }) => {
  const scaleAnim = React.useRef(new Animated.Value(1)).current;

  React.useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(scaleAnim, {
          toValue: 1.03,
          duration: 800,
          useNativeDriver: true,
          easing: Easing.inOut(Easing.ease),
        }),
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
          easing: Easing.inOut(Easing.ease),
        }),
      ]),
    ).start();
  }, [scaleAnim]);

  return (
    <Animated.View style={{ transform: [{ scale: scaleAnim }], alignSelf: isMobile ? 'stretch' : 'flex-start' }}>
      <CCButton
        onPress={onPress}
        type={'outline'}
      >
        <CCText fontSize={isMobile ? FontSizes[13] : FontSizes[12]} fontWeight="bold" color={'#EBB368'} style={{ letterSpacing: 0.5 }}>
          🎁 Solve Puzzles: Earn Discount coupon
        </CCText>
      </CCButton>
    </Animated.View>
  );
};

interface RenewalBannerProps {
  daysLeft: number;
  sessionsLeft: number;
  couponCode?: string;
  expiryDate?: string;
  onRenewNow?: () => void;
  couponDiscountPercentage?: number;
  onClose?: () => void;
  challengeCompleted?: boolean;
  onEarnDiscount?: () => void;
}

const RenewalBanner: React.FC<RenewalBannerProps> = ({
  daysLeft,
  sessionsLeft,
  expiryDate,
  couponDiscountPercentage = 0,
  couponCode = '',
  onRenewNow,
  onClose,
  challengeCompleted,
  onEarnDiscount,
}) => {
  const { isMobileOrTab } = useMediaQuery();
  const navigation = useNavigation();
  const renewState =
    daysLeft >= 3 && daysLeft < 7
      ? 'renew'
      : daysLeft >= 0 && daysLeft < 3
        ? 'expiring'
        : daysLeft < 0
          ? 'expired'
          : null;
  const isRenewNeeded = renewState === 'renew';
  const isExpired = renewState === 'expired';
  const handleRenewNow = () => {
    // if (onRenewNow) {
    //   onRenewNow();
    // } else {
    // Navigate to renewal screen with coupon code if available
    (navigation as any).navigate(ScreenName.Renewal, {
      couponCode: couponCode,
    });
    // }
  };

  const handleCopyCoupon = () => {
    if (couponCode && typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(couponCode);
      // You might want to show a toast notification here
    }
  };

  // Background color from Figma: #4D3F37
  const backgroundColor = useMemo(
    () => {
      console.log('renewState: ', renewState);
      console.log('daysLeft: ', daysLeft);
      return renewState === 'expired'
        ? CCColors.Error
        : renewState === 'expiring'
          ? CCColors.Red
          : CCColors.TextColor.Dark
    }
    ,
    [renewState, daysLeft],
  ); // #4D3F37

  // Text color - white on dark background
  const textColor = CCColors.White;

  // Gradient border colors: #E17846 to rgba(225, 120, 70, 0.24) at 270deg (right to left)
  // border-image-source: linear-gradient(270deg, #E17846 0%, rgba(225, 120, 70, 0.24) 100%)
  const borderGradientColors = ['#E17846', 'rgba(225, 120, 70, 0.24)'];

  // Light beige for coupon code box
  const couponBoxBg = '#F5E6D3'; // Light beige color
  if (daysLeft > 7) {
    return null;
  }
  return (
    <View style={styles.shadowWrapper}>
      <Box
        style={[
          styles.container,
          {
            backgroundColor,
            borderRadius: 8,
          },
        ]}>
        <Box style={{ position: 'relative', marginHorizontal: 8, marginTop: 6 }}>
          {isMobileOrTab ? (
            /* Mobile Layout: Two rows */
            <>
              {/* First Row: Image and Text */}
              <Box
                flexDirection="row"
                alignItems="flex-start"
                style={{ gap: 8, marginBottom: 16 }}>
                {/* Left Side - Image */}
                <Box style={{ width: 80, height: 80, flexShrink: 0 }}>
                  <Image
                    source={require('@/Assets/Images/Meetings/csoc_plan_expiring.svg')}
                    style={{
                      width: 80,
                      height: 80,
                      resizeMode: 'contain',
                    }}
                  />
                </Box>

                {/* Center - Main Text */}
                <Box
                  flex={1}
                  style={{
                    paddingLeft: 4,
                  }}>
                  {isExpired ? (
                    <CCText
                      fontSize={FontSizes[14]}
                      fontWeight="bold"
                      color={textColor}
                      style={{ lineHeight: 20, textAlign: 'left' }}>
                      Your plan expired on{' '}
                      {new Date(expiryDate || '').toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                      . Renew now to continue your sessions
                    </CCText>
                  ) : (
                    <CCText
                      fontSize={FontSizes[14]}
                      fontWeight="bold"
                      color={textColor}
                      style={{ lineHeight: 20, textAlign: 'left' }}>
                      Your Plan expires after {sessionsLeft} sessions in{' '}
                      {daysLeft} days. Renew early and save EXTRA {couponDiscountPercentage}%!
                    </CCText>
                  )}
                </Box>
              </Box>

              {/* Second Row: Coupon and Renew Button */}
              <Box flexDirection="column" gap={12}>
                {couponCode && (
                  <Box
                    style={{
                      backgroundColor: CCColors.White,
                      borderRadius: 8,
                      padding: 10,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.1,
                      shadowRadius: 4,
                      elevation: 3,
                    }}>
                    {/* Coupon Code Box with Badge */}
                    <Box
                      flexDirection="row"
                      alignItems="center"
                      gap={8}
                      style={{
                        width: '100%',
                        borderWidth: 2,
                        paddingHorizontal: 10,
                        paddingVertical: 10,
                        borderRadius: 6,
                        backgroundColor: CCColors.Primary.lightBrown,
                        borderStyle: 'solid',
                        borderColor: CCColors.Primary.Default,
                      }}>
                      <Box
                        flexDirection="row"
                        alignItems="center"
                        gap={6}
                        flex={1}>
                        <MaterialCommunityIcons
                          name="ticket-percent"
                          size={16}
                          color={CCColors.Primary.Default}
                        />
                        <CCText
                          fontSize={FontSizes[13]}
                          fontWeight="bold"
                          color={CCColors.TextColor.Dark}
                          style={{ flexShrink: 1 }}>
                          {couponCode}
                        </CCText>
                      </Box>
                      {/* Discount Badge */}
                      <Box
                        style={{
                          paddingHorizontal: 6,
                          paddingVertical: 3,
                          backgroundColor: CCColors.Green,
                          borderRadius: 4,
                          flexShrink: 0,
                        }}>
                        <CCText
                          fontSize={FontSizes[9]}
                          fontWeight="bold"
                          color={CCColors.White}>
                          10% OFF
                        </CCText>
                      </Box>
                      <Pressable onPress={handleCopyCoupon}>
                        <MaterialIcons
                          name="content-copy"
                          size={16}
                          color={CCColors.Primary.Default}
                        />
                      </Pressable>
                    </Box>
                  </Box>
                )}
                <CCButton
                  onPress={handleRenewNow}
                  style={{
                    paddingVertical: 12,
                    width: '100%',
                  }}>
                  <CCText fontSize={FontSizes[14]} fontWeight="bold">
                    Renew Now
                  </CCText>
                </CCButton>
                {!challengeCompleted && onEarnDiscount && (
                  <AnimatedPuzzleButton onPress={onEarnDiscount} isMobile={true} />
                )}
              </Box>
            </>
          ) : (
            /* Desktop Layout: Single row with all elements */
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              style={{ gap: 12 }}>
              {/* Left Side - Gift Boxes with Icons */}
              <Box>
                <Image
                  source={require('@/Assets/Images/Meetings/csoc_plan_expiring.svg')}
                  style={{
                    width: '20vw' as any,
                    height: '20vh' as any,
                    maxWidth: 210,
                    maxHeight: 156,
                    bottom: -9,
                    left: 0,
                  }}
                />
              </Box>

              {/* Center - Main Text */}
              <Box
                flex={1}
                style={{
                  paddingHorizontal: 15,
                }}>
                {isExpired ? (
                  <CCText
                    fontSize={FontSizes[20]}
                    fontWeight="bold"
                    color={textColor}
                    style={styles.mainText}>
                    Your plan expired on{' '}
                    {new Date(expiryDate || '').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                    . Renew now to continue your sessions
                  </CCText>
                ) : (
                  <CCText
                    fontSize={FontSizes[20]}
                    fontWeight="bold"
                    color={textColor}
                    style={styles.mainText}>
                    Your Plan expires after {sessionsLeft} sessions in{' '}
                    {daysLeft} days Renew early and save EXTRA {couponDiscountPercentage}%!
                  </CCText>
                )}
              </Box>
              <Box gap={10}>
                {/* Right Side - Coupon and Button */}
                {couponCode && (
                  <Box
                    flexDirection="column"
                    alignItems="flex-start"
                  // style={{
                  //   backgroundColor: CCColors.White,
                  //   borderRadius: 8,
                  //   padding: 8,
                  //   // minWidth: 180,
                  //   shadowColor: '#000',
                  //   shadowOffset: {width: 0, height: 2},
                  //   shadowOpacity: 0.1,
                  //   shadowRadius: 4,
                  //   elevation: 3,
                  // }}
                  >
                    {/* Coupon Code Box with Badge */}
                    <Box
                      flexDirection="row"
                      alignItems="center"
                      gap={8}
                      style={{
                        // width: '100%',
                        borderWidth: 2,
                        borderStyle: 'dashed',
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 6,
                        backgroundColor: CCColors.Primary.lightBrown,
                        // borderStyle: 'solid',
                        borderColor: CCColors.Primary.Default,
                      }}>
                      <Box
                        flexDirection="row"
                        alignItems="center"
                        gap={6}
                        flex={1}>
                        <MaterialCommunityIcons
                          name="ticket-percent"
                          size={18}
                          color={CCColors.Primary.Default}
                        />
                        <CCText
                          fontSize={FontSizes[12]}
                          fontWeight="bold"
                          color={CCColors.TextColor.Dark}>
                          {couponCode}
                        </CCText>
                      </Box>
                      {/* Discount Badge */}
                      <Box
                        style={{
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          backgroundColor: CCColors.Green,
                          borderRadius: 4,
                        }}>
                        {couponDiscountPercentage > 0 && <CCText
                          fontSize={FontSizes[10]}
                          fontWeight="bold"
                          color={CCColors.White}> Extra {couponDiscountPercentage}% OFF
                        </CCText>}
                      </Box>
                      <Pressable onPress={handleCopyCoupon}>
                        <MaterialIcons
                          name="content-copy"
                          size={18}
                          color={CCColors.Primary.Default}
                        />
                      </Pressable>
                    </Box>
                  </Box>
                )}
                <CCButton onPress={handleRenewNow} style={{ paddingVertical: 4 }}>
                  <CCText fontSize={FontSizes[12]} fontWeight="bold">
                    Renew Now
                  </CCText>
                </CCButton>
                {!challengeCompleted && onEarnDiscount && (
                  <AnimatedPuzzleButton onPress={onEarnDiscount} isMobile={false} />
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Terms & Conditions */}
        {/* <Box
            style={{
              paddingHorizontal: 16,
              paddingBottom: 12,
              paddingTop: 8,
              borderTopWidth: 1,
              borderTopColor: 'rgba(255, 255, 255, 0.1)',
            }}>
            <CCText
              fontSize={FontSizes[11]}
              color="rgba(255, 255, 255, 0.7)"
              textAlign="center">
              Terms & conditions apply
            </CCText>
          </Box>
          */}
      </Box>
    </View>
  );
};

const styles = StyleSheet.create({
  shadowWrapper: {
    marginBottom: 15,
    borderRadius: 8,
    // box-shadow: 0px 4px 8px 0px #4D3F370D
    // 0D in hex = 13 in decimal, 13/255 = 0.05098
    shadowColor: '#4D3F37',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 13 / 255, // Exact opacity from #4D3F370D
    shadowRadius: 8,
    elevation: 4,
  },
  gradientBorder: {
    borderRadius: 8,
    // border: 1px solid - created by padding: 1
    padding: 1,
  },
  container: {
    borderRadius: 8,
  },
  mainText: {
    lineHeight: 32,
    textAlign: 'left',
  },
  giftBox: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lightRays: {
    opacity: 0.8,
  },
  floatingIcon: {
    position: 'absolute',
    opacity: 0.9,
  },
  renewButton: {
    backgroundColor: CCColors.FadedYellow, // Yellow button
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    minWidth: 120,
  },
  renewButtonContent: {
    padding: 0,
  },
  closeButton: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    padding: 5,
    zIndex: 10,
  },
});

export default RenewalBanner;
